import {z} from "zod"

export const RESOURCE_FIELDS = ["imageFile", "repeatImageFile", "materialXFile"] as const
export type ResourceField = (typeof RESOURCE_FIELDS)[number]

/**
 * Fields this client knows the Material Database has no slot for yet. They are in RESOURCE_FIELDS and in the
 * resource schema because the shape is agreed; sending one returns `invalid-field-name` inside a 200
 * and fails the whole request, taking any good files with it. So they are accepted from the caller
 * and discarded here — everything upstream builds them for real.
 *
 * The day Configura ship the MaterialX slot, deleting the entry is the whole change.
 */
export const UNSUPPORTED_RESOURCE_FIELDS: readonly ResourceField[] = ["materialXFile"]

const CONTENT_RESOURCE_TYPE = "Textile"

const textileResourceSchema = z.object({
    "original-filename": z.string().optional(),
    mimetype: z.string().optional(),
    filename: z.string().optional(),
})

const textileSchema = z.object({
    id: z.string(),
    version: z.number(),
    sku: z.string(),
    name: z.string().nullish(),
    repeatH: z.number().nullish(),
    repeatV: z.number().nullish(),
    discontinued: z.boolean().nullish(),
    company: z.object({id: z.string(), name: z.string().optional()}),
    resources: z
        .object({
            imageFile: textileResourceSchema.nullish(),
            repeatImageFile: textileResourceSchema.nullish(),
            materialXFile: textileResourceSchema.nullish(),
        })
        .default({}),
})

const textileSearchResultSchema = z.object({
    total: z.number(),
    size: z.number(),
    from: z.number(),
    results: z.array(textileSchema),
})

const updateTextileResponseSchema = z.object({id: z.string()})

/**
 * The create request, read from the raw `openapi.json` (2026-08-27): 25 required fields, and the
 * Material Database declares `additionalProperties: false` — `strict()` mirrors that, so an unknown key fails
 * here instead of as a Material Database 4XX. The honest-empty forms (`""`, `[]`, `railroadDir: "N"`) are
 * accepted for everything but the eight numeric fields — verified by a live create.
 */
const textileCreationSchema = z
    .object({
        companyId: z.string(),
        sku: z.string(),
        name: z.string(),
        discontinued: z.boolean(),
        category: z.string(),
        pattern: z.string(),
        weight: z.string(),
        // (N)one, (B)oth, (D)irectional, (R)ailroaded — the one enum-valued creation field.
        railroadDir: z.enum(["N", "B", "D", "R"]),
        width: z.string(),
        finish: z.string(),
        backing: z.string(),
        durability: z.string(),
        colorFastness: z.string(),
        application: z.string(),
        subCategory: z.array(z.string().min(1)),
        content: z.array(z.string().min(1)),
        cleaning: z.array(z.string().min(1)),
        flameResist: z.array(z.string().min(1)),
        pillingResist: z.number(),
        seamWarp: z.number(),
        seamFill: z.number(),
        tensileWarp: z.number(),
        tensileFill: z.number(),
        price: z.number(),
        repeatH: z.number(),
        repeatV: z.number(),
        brand: z.string().optional(),
        colorways: z.array(z.string()).optional(),
        rei: z.array(z.string()).optional(),
    })
    .strict()

const createTextileResponseSchema = z.object({id: z.string(), version: z.number()})

const uploadResponseSchema = z.object({
    data: z.record(z.string(), textileResourceSchema).default({}),
    errors: z.record(z.string(), z.string()).default({}),
})

export type TextileResource = z.infer<typeof textileResourceSchema>
export type Textile = z.infer<typeof textileSchema>
export type TextileSearchResult = z.infer<typeof textileSearchResultSchema>
export type TextileCreation = z.infer<typeof textileCreationSchema>

export type TextilePatch = {
    repeatH?: number
    repeatV?: number
}

export type ResourceUpload = {
    body: () => Promise<Blob>
    fileName: string
}

export type UploadResourcesResult = {
    /** Fields the caller supplied that the Material Database has no slot for; built, then dropped unsent. */
    discarded: ResourceField[]
}

export class ConfiguraMaterialDatabaseError extends Error {
    /** HTTP status when the failure was an error response — a 409 from `createTextile` means the sku already exists. */
    readonly status?: number

    constructor(message: string, options?: {status?: number}) {
        super(message)
        this.name = "ConfiguraMaterialDatabaseError"
        this.status = options?.status
    }
}

export type ConfiguraMaterialDatabaseClientOptions = {
    /** Environment config, never a constant — dev and prod differ. Read at the composition root. */
    baseUrl: string
    apiKey: string
    fetchImpl?: typeof fetch
    /** Defaults to UNSUPPORTED_RESOURCE_FIELDS; override to drive the real path, as the tests do. */
    unsupportedResourceFields?: readonly ResourceField[]
}

/** Escapes an OData string literal: single quotes are doubled. Article IDs are customer data. */
export function escapeODataLiteral(value: string): string {
    return value.replaceAll("'", "''")
}

export class ConfiguraMaterialDatabaseHttpClient {
    private readonly baseUrl: string
    private readonly apiKey: string
    private readonly fetchImpl: typeof fetch
    private readonly unsupportedResourceFields: readonly ResourceField[]

    constructor(options: ConfiguraMaterialDatabaseClientOptions) {
        this.baseUrl = options.baseUrl.replace(/\/+$/, "")
        this.apiKey = options.apiKey
        this.fetchImpl = options.fetchImpl ?? fetch
        this.unsupportedResourceFields = options.unsupportedResourceFields ?? UNSUPPORTED_RESOURCE_FIELDS
    }

    async findTextilesBySku(sku: string, options?: {top?: number; skip?: number; companyId?: string}): Promise<TextileSearchResult> {
        const clauses = [`sku eq '${escapeODataLiteral(sku)}'`]
        if (options?.companyId !== undefined) {
            clauses.push(`company/id eq '${escapeODataLiteral(options.companyId)}'`)
        }
        const params = new URLSearchParams({$filter: clauses.join(" and ")})
        if (options?.top !== undefined) {
            params.set("$top", String(options.top))
        }
        if (options?.skip !== undefined) {
            params.set("$skip", String(options.skip))
        }
        // Zero hits is a 200 with total: 0, so "not found" never reaches the error path here.
        const response = await this.request("GET", `/v2/textiles/?${params.toString()}`)
        return this.parse(textileSearchResultSchema, await response.json(), "textile search")
    }

    /** Multi-sku search; `total` aggregates across skus, so per-sku multiplicity must be regrouped from one full page (`$top` defaults to the maximum, chunk well below it) — see `splitMultiSkuSearch`. */
    async findTextilesBySkus(skus: readonly string[], options?: {top?: number; companyId?: string}): Promise<TextileSearchResult> {
        // No skus, no filter, no request — an unconstrained search would return other textiles.
        if (skus.length === 0) {
            return {total: 0, size: 0, from: 0, results: []}
        }
        const skuClause = skus.map((sku) => `sku eq '${escapeODataLiteral(sku)}'`).join(" or ")
        const clauses = options?.companyId !== undefined ? [`(${skuClause})`, `company/id eq '${escapeODataLiteral(options.companyId)}'`] : [skuClause]
        const params = new URLSearchParams({$filter: clauses.join(" and "), $top: String(options?.top ?? 100)})
        const response = await this.request("GET", `/v2/textiles/?${params.toString()}`)
        return this.parse(textileSearchResultSchema, await response.json(), "textile search")
    }

    async getTextile(id: string, options?: {expectedCompanyId?: string}): Promise<Textile> {
        const response = await this.request("GET", `/v2/textiles/${encodeURIComponent(id)}`)
        const textile = this.parse(textileSchema, await response.json(), "textile")
        // The company guard, extended from the search to direct reads: a binding whose textileId
        // points into another company — however it came to — must fail the read here rather than
        // proceed to a cross-tenant write.
        if (options?.expectedCompanyId !== undefined && textile.company.id !== options.expectedCompanyId) {
            throw new ConfiguraMaterialDatabaseError(
                `Textile ${id} belongs to company ${textile.company.id}, not the expected company ${options.expectedCompanyId}`,
            )
        }
        return textile
    }

    /**
     * `POST /v2/textiles/` is a permanent write — the Material Database has no DELETE — so the request is
     * validated before sending. A 409 (`error.status`) means the sku already exists in the company;
     * the caller re-runs the company-scoped search and binds to the entry that won.
     */
    async createTextile(creation: TextileCreation): Promise<{id: string; version: number}> {
        const validated = textileCreationSchema.safeParse(creation)
        if (!validated.success) {
            throw new ConfiguraMaterialDatabaseError(`Invalid textile creation request: ${validated.error.message}`)
        }
        const response = await this.request("POST", "/v2/textiles/", {
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(validated.data),
        })
        return this.parse(createTextileResponseSchema, await response.json(), "textile creation")
    }

    async updateTextile(id: string, version: number, patch: TextilePatch): Promise<{id: string}> {
        const response = await this.request("PUT", `/v2/textiles/${encodeURIComponent(id)}/${version}`, {
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify(patch),
        })
        return this.parse(updateTextileResponseSchema, await response.json(), "textile update")
    }

    /** One request per field, all against the caller's `version` (publishing never mints one), so only one file is ever resident; returns the fields dropped unsent, and throws — leaving later fields unsent — if anything sent did not land. */
    async uploadResources(id: string, version: number, files: Partial<Record<ResourceField, ResourceUpload>>): Promise<UploadResourcesResult> {
        const discarded: ResourceField[] = []
        const path = `/v2/content/${encodeURIComponent(id)}/${version}/resource/${CONTENT_RESOURCE_TYPE}`
        for (const field of RESOURCE_FIELDS) {
            const file = files[field]
            if (!file) {
                continue
            }
            if (this.unsupportedResourceFields.includes(field)) {
                discarded.push(field)
                continue
            }
            const form = new FormData()
            form.append(field, await file.body(), file.fileName)
            const response = await this.request("POST", path, {body: form})
            const result = this.parse(uploadResponseSchema, await response.json(), "resource upload")

            const fieldErrors = result.errors
            if (Object.keys(fieldErrors).length > 0) {
                const detail = Object.entries(fieldErrors)
                    .map(([erroredField, reason]) => `${erroredField}: ${reason}`)
                    .join(", ")
                throw new ConfiguraMaterialDatabaseError(`Upload of ${field} reported per-field errors (${detail})`)
            }
            if (!(field in result.data)) {
                throw new ConfiguraMaterialDatabaseError(`Upload response did not report an outcome for ${field}`)
            }
        }
        return {discarded}
    }

    private async request(method: string, path: string, init?: {headers?: Record<string, string>; body?: BodyInit}): Promise<Response> {
        // Content-Type is deliberately left unset for FormData bodies so fetch can add the boundary.
        const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
            method,
            headers: {"X-API-Key": this.apiKey, ...init?.headers},
            body: init?.body,
        })
        if (!response.ok) {
            throw await this.errorForResponse(method, path, response)
        }
        return response
    }

    private async errorForResponse(method: string, path: string, response: Response): Promise<ConfiguraMaterialDatabaseError> {
        const body = await response.text().catch(() => "")
        return new ConfiguraMaterialDatabaseError(
            `${method} ${path} failed with ${response.status} ${response.statusText}${body ? `: ${body.slice(0, 500)}` : ""}`,
            {
                status: response.status,
            },
        )
    }

    private parse<S extends z.ZodTypeAny>(schema: S, payload: unknown, what: string): z.infer<S> {
        const parsed = schema.safeParse(payload)
        if (!parsed.success) {
            throw new ConfiguraMaterialDatabaseError(`Unexpected ${what} response shape: ${parsed.error.message}`)
        }
        return parsed.data
    }
}
